import { memo } from "react";
import { Handle, Position } from "reactflow";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { parseLocalDate } from "@/lib/movemap";

const VARIANT = {
  root: "bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-600/30",
  ingreso: "bg-emerald-50 dark:bg-emerald-950/60 border-emerald-300 dark:border-emerald-700 text-emerald-900 dark:text-emerald-100",
  egreso: "bg-rose-50 dark:bg-rose-950/60 border-rose-300 dark:border-rose-700 text-rose-900 dark:text-rose-100",
  haber: "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-500 text-slate-900 dark:text-slate-100",
  year: "bg-orange-500 border-orange-400 text-white shadow-md shadow-orange-500/30",
  month: "bg-amber-50 dark:bg-slate-900 border-amber-300 dark:border-amber-600/50 text-slate-900 dark:text-slate-100",
  day: "bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100",
  cambio: "bg-sky-50 dark:bg-sky-950/60 border-sky-300 dark:border-sky-700 text-sky-900 dark:text-sky-100",
};

export const MoveMapNode = memo(({ id, data }) => {
  const cls = VARIANT[data.variant] || VARIANT.month;
  const big = data.big;
  return (
    <div
      className={`rounded-xl border p-3 flex flex-col gap-1 backdrop-blur-sm transition-all ${cls} ${big ? "min-w-[190px]" : "min-w-[150px] max-w-[240px]"}`}
      data-testid={`movenode-${id}`}
    >
      <Handle type="target" position={Position.Left} className="!bg-slate-400 !w-2 !h-2 !border-0" />
      <Handle type="source" position={Position.Right} className="!bg-slate-400 !w-2 !h-2 !border-0" />

      <div className={`font-heading font-semibold break-words leading-snug ${big || data.variant === "root" || data.variant === "year" ? "text-base" : "text-sm"}`}>
        {data.title}
      </div>

      {data.subtitle && (
        <div className={`font-mono2 ${big ? "text-lg font-bold" : "text-xs"} ${data.variant === "haber" ? (data.saldoPositive ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400") : "opacity-90"}`}>
          {data.subtitle}
        </div>
      )}

      {data.lines && (
        <div className="flex flex-col gap-0.5 mt-0.5">
          {data.lines.map((l, i) => (
            <span key={i} className={`font-mono2 text-[11px] ${i === 0 ? "text-emerald-600 dark:text-emerald-400" : i === 1 ? "text-rose-600 dark:text-rose-400" : (data.saldoPositive ? "text-slate-700 dark:text-slate-200 font-semibold" : "text-rose-600 dark:text-rose-400 font-semibold")}`}>
              {l}
            </span>
          ))}
        </div>
      )}

      {data.category && (
        <span className="inline-flex self-start items-center text-[9px] uppercase tracking-wide font-mono2 px-1.5 py-0.5 rounded bg-black/10 dark:bg-white/10">
          {data.category}
        </span>
      )}
      {data.desc && <span className="font-body text-[11px] opacity-75 break-words leading-snug">{data.desc}</span>}
      {data.note && <span className="font-mono2 text-[10px] opacity-60">{data.note}</span>}
      {data.createdAt && (
        <span className="font-mono2 text-[9px] opacity-60">
          {format(parseLocalDate(data.createdAt), "d MMM yyyy", { locale: es })}
        </span>
      )}
    </div>
  );
});

MoveMapNode.displayName = "MoveMapNode";
